import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import moment from 'moment';
import { parseISO } from 'date-fns';
import { Autocomplete, TextField } from '@mui/material';
import { MDatePicker } from '../../@material-extend';
import { snakeToKebab, escapeSpecialCharacters } from '../../../utils/formatString';

/**
 * A flexible form item component designed to generate different types of inputs controlled by React Hook Form.
 * It supports text, date, and select inputs, with additional customizations for autocomplete, multi-line, and multiple selections.
 * This component automatically integrates input validation and error handling based on the provided `control` and `errors` props.
 *
 * @param {object} props - The properties passed to the FormItem component.
 * @param {string} [props.autoComplete='new_password'] - Sets the autocomplete attribute to prevent browser autofill.
 * @param {object} props.control - The `control` object provided by React Hook Form for managing form state.
 * @param {object} props.errors - The `errors` object from React Hook Form for displaying validation messages.
 * @param {boolean} [props.fullWidth=false] - If `true`, the input takes the full width of its container.
 * @param {string} props.label - The label text for the input.
 * @param {boolean} [props.mandatory=false] - Marks the input as mandatory, appending '(requis)' to the label text.
 * @param {number} [props.maxLength=0] - Sets the maximum length of the input value. Applies only to text inputs.
 * @param {boolean} [props.multiline=false] - Enables multi-line input. Applies only to text inputs.
 * @param {boolean} [props.multiple=false] - Enables multiple selections. Applies to select inputs and Autocomplete.
 * @param {string} props.name - The name attribute of the input. Used for form submission and validation.
 * @param {Array} [props.options=[]] - Options for select inputs and Autocomplete. Each option is an object with at least `label` and `value` properties.
 * @param {string} [props.optionName='label'] - Object property name to display as the option label in select inputs.
 * @param {string} [props.optionControl='label'] - Object property name that uniquely identifies an option. Used for matching selected options.
 * @param {string} [props.placeholder] - Placeholder text for the input.
 * @param {function} [props.renderOption] - Custom render function for Autocomplete option.
 * @param {number} [props.rows=1] - The number of visible text rows for multiline inputs.
 * @param {function} [props.setValue] - The `setValue` method from React Hook Form to manually set input values.
 * @param {object} [props.sx] - Custom styles to apply to the input component.
 * @param {string} [props.type='text'] - Specifies the type of input to render ('text', 'date', 'tel', 'number', 'email', 'password', etc.).
 * @returns {JSX.Element} The generated form item input component based on the specified type and configuration.
 */

const FormItem = (props) => {
  const {
    autoComplete = 'new_password', // disable autocomplete ('off' is not working properly)
    control,
    errors,
    fullWidth = false,
    label,
    mandatory = false,
    maxLength = 0,
    multiline = false,
    multiple = false,
    name = '', // name of the form field (ex for resource : residenceCountry)
    options = [],
    optionName = 'label', // select only : name of the array attribute to display (ex for countries : name)
    optionControl = 'label', // select only : name of the array attribute to match on (ex for countries : iso_code)
    placeholder,
    renderOption, // select only : component to render in options popup menu
    rows = 1, // select only : rows quantity in multiple mode
    setValue,
    sx,
    type = 'text' // text, date, tel, number, email, password, etc.
  } = props;
  const id = `form-input-${snakeToKebab(name)}`;
  const formattedLabel = `${label}${mandatory ? ' (requis)' : ''}`;
  let component = null;

  const handleChangeText = (e, onChange) => {
    if (maxLength && e.target.value.length > maxLength + 1) {
      e.target.value = e.target.value.slice(0, maxLength + 1);
    }
    if (onChange) {
      onChange(e);
    }
    if (setValue) {
      setValue(name, escapeSpecialCharacters(e.target.value), { shouldTouch: true, shouldValidate: true });
    }
  };

  const handleChangeDate = (newValue, onChange) => {
    const momentDate = moment(newValue);
    const formattedDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : '';

    if (onChange) {
      onChange(formattedDate);
    }
    if (setValue) {
      setValue(name, formattedDate, { shouldTouch: true, shouldValidate: true });
    }
  };

  const handleChangeAutocomplete = (newValue, onChange) => {
    const formattedNewValue = multiple
      ? newValue.map((item) => item[optionControl])
      : (newValue?.[optionControl] ?? '');
    if (onChange) {
      onChange(formattedNewValue);
    }
    if (setValue) {
      setValue(name, formattedNewValue, { shouldTouch: true, shouldValidate: true });
    }
  };

  const renderTextField = ({ field: { onChange, value, ...fieldProps } }) => (
    <TextField
      {...fieldProps}
      id={id}
      autoComplete={autoComplete}
      label={formattedLabel}
      placeholder={placeholder}
      multiline={type === 'text' && multiline}
      rows={rows}
      sx={{ flexGrow: fullWidth ? 1 : 0, ...sx }}
      type={type}
      value={value ?? ''}
      variant="outlined"
      error={!!errors?.[name]}
      helperText={errors?.[name]?.message}
      onChange={(e) => handleChangeText(e, onChange)}
    />
  );

  if (type === 'date') {
    component = (
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value, ...fieldProps } }) => {
          // Convertir la valeur en chaîne de caractères si c'est un objet `Date`.
          const parsedValue = typeof value === 'string' ? parseISO(value) : value;

          return (
            <MDatePicker
              {...fieldProps}
              label={formattedLabel}
              sx={sx}
              value={parsedValue}
              size="small"
              error={!!errors?.[name]}
              helperText={errors?.[name]?.message}
              fullWidth
              onChange={(newValue) => handleChangeDate(newValue, onChange)}
            />
          );
        }}
      />
    );
  } else if (options?.length) {
    component = (
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          // Value management depends on whether the Autocomplete is in multiple mode or not
          let value = null;
          if (multiple) {
            value = options.filter((option) => (field?.value ? field.value.includes(option[optionControl]) : null));
          } else {
            value = options.find((option) => option[optionControl] === field.value) || null;
          }

          return (
            <Autocomplete
              {...field}
              id={id}
              getOptionLabel={(option) => option[optionName] || ''}
              isOptionEqualToValue={(option, value) => option[optionControl] === value[optionControl]}
              multiple={multiple}
              options={options}
              renderInput={(params) => renderTextField({ field: params }, errors?.[name], errors?.[name]?.message)}
              renderOption={renderOption}
              sx={{ flexGrow: fullWidth ? 1 : 0 }}
              value={value}
              onChange={(_, newValue) => handleChangeAutocomplete(newValue, field.onChange)}
            />
          );
        }}
      />
    );
  } else {
    component = <Controller control={control} name={name} render={renderTextField} />;
  }
  return component;
};

FormItem.propTypes = {
  autoComplete: PropTypes.string,
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  fullWidth: PropTypes.bool,
  getValues: PropTypes.func,
  label: PropTypes.string.isRequired,
  mandatory: PropTypes.bool,
  maxLength: PropTypes.number,
  multiline: PropTypes.bool,
  multiple: PropTypes.bool,
  name: PropTypes.string.isRequired,
  options: PropTypes.array,
  optionName: PropTypes.string,
  optionControl: PropTypes.string,
  placeholder: PropTypes.string,
  renderOption: PropTypes.func,
  rows: PropTypes.number,
  setValue: PropTypes.func,
  sx: PropTypes.object,
  type: PropTypes.string
};

export default FormItem;
